import React, { ReactNode, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { GroupedVirtuoso } from 'react-virtuoso';
import { Grid, Box, Text } from '@cad-arc';
import { QueueDisplayDensityEnum, UnitQueueView } from '@mark43/cad-api';
import { useQueueStore } from '../queues/context/use-queue-store';
import { ASSIGNED_USER_UNIT } from './constants';
import { FrUnitListItem } from './fr-unit-list-item';
import { EMPTY_GROUP_SENTINEL_HEADER } from '../queues/store/queue-store';
import { FrQueueColumnHeaders } from '../common/components/fr-queues/fr-queue-column-headers';
import { FrUnitsCompactQueueRow } from './fr-units-list-page/fr-compact-queue-row';

export const FrUnitsList = observer(function FrUnitsList({ compact }: { compact?: boolean }) {
    const store = useQueueStore();

    const list = store.listStore.data.list;
    const groupCounts = store.listStore.data.groupCounts;
    const groupHeaders = store.listStore.data.groupHeaders;
    const { displayDensity, queueColumns } = store.workspacePanel.queue;

    const isDisplayDensityCompact = displayDensity === QueueDisplayDensityEnum.COMPACT.name;

    const columnBgColor = useMemo(() => {
        if (isDisplayDensityCompact && !compact) {
            return 'surface.foreground';
        }
        return compact ? 'surface.foreground' : 'surface.background';
    }, [compact, isDisplayDensityCompact]);

    const renderGroupContent = (groupIndex: number): ReactNode => {
        const groupHeader = groupHeaders[groupIndex]
            .replace('Pending', 'Available')
            .replace(EMPTY_GROUP_SENTINEL_HEADER, '(None)');

        if (groupHeader === ASSIGNED_USER_UNIT) {
            // Hide user's unit group header as per design
            return (
                <>
                    <Box h="1px" userSelect="none">
                        &nbsp;
                    </Box>
                    {isDisplayDensityCompact && !compact && (
                        <FrQueueColumnHeaders workspaceQueue={store.workspacePanel.queue} />
                    )}
                </>
            );
        }
        return (
            <Grid key={groupIndex} w="100%">
                <Box
                    data-testid="fr-units-list-group-header"
                    p={2}
                    w="100%"
                    backgroundColor={columnBgColor}
                >
                    <Text variant="headingMd">{`${groupHeader} (${groupCounts[groupIndex]})`}</Text>
                </Box>
                {isDisplayDensityCompact && !compact && (
                    <FrQueueColumnHeaders workspaceQueue={store.workspacePanel.queue} />
                )}
            </Grid>
        );
    };

    const renderItemContent = (index: number): ReactNode => {
        const unit = list[index] as UnitQueueView;

        if (!unit) return null;

        if (isDisplayDensityCompact && !compact) {
            return <FrUnitsCompactQueueRow data={unit} index={index} queueColumns={queueColumns} />;
        }

        return <FrUnitListItem unit={unit} />;
    };

    return (
        <GroupedVirtuoso
            key={`units-list-${list.length}`}
            style={{
                height: '100%',
                width: '100%',
                overflowY: 'auto',
                backgroundColor: isDisplayDensityCompact
                    ? 'var(--arc-colors-surface-foreground)'
                    : 'transparent',
                '--columns': queueColumns.map((col) => `${col.width}px`).join(' '),
            }}
            groupCounts={groupCounts}
            computeItemKey={(index: number) => index}
            overscan={200}
            itemContent={renderItemContent}
            groupContent={renderGroupContent}
        />
    );
});
